import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import "./Scss/InformesGStyles.scss";

import {
  columnsTableContacts,
  columnsTableClients,
  columnsTableAsocZone,
} from "./Components/Tables";
import { columnsTableContracts } from "./Components/Tables";

import {
  CasesLineChart,
  CasesPieCharts,
  CasesFlowStatusMatrixTable,
  ContactsLineChart,
  ContactsPieChart,
  ContractsPieChart,
  ContactsConversionBarChart,
  ContactsStatusStackedBarChart,
  ClientsMonthlyLineChart,
  CasesVsContractsLineChart,
} from "./Components/Charts";
import { useUser } from "../Components/Context/UserProvider";
import { CommonSubHeader } from "../Components/helpers/Helpers";
import Table from "../Components/Common/Table/Table";

import {
  Tabs,
  Tab,
  Box,
  Fade,
  Grid,
  TextField,
  MenuItem,
  Button,
  Paper,
  Typography,
  LinearProgress,
  Divider,
  Skeleton,
} from "@mui/material";

import api from "../api/axios";
import ReactPaginate from "react-paginate";
import { ChevronLeft, ChevronRight } from "lucide-react";
import AnimatedCounter from "../Components/AnimatedCounter";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { fontSize, width } from "@mui/system";
import { AddCircle, Functions, RemoveCircle } from "@mui/icons-material";

/* ------------------------------ */
/* Helpers                        */
/* ------------------------------ */

const fullWidthTabsStyles = {
  mb: 3,
  borderRadius: "10px",
  background: "rgba(255,255,255,0.05)",
  width: "100%",
  color: "white",
  "& .MuiTab-root": {
    textTransform: "none",
    fontWeight: 500,
    color: "#9ca3af",
    transition: "0.3s",
  },

  "& .Mui-selected": {
    color: "#ffffff !important",
  },

  "& .MuiTabs-indicator": {
    height: "4px",
    borderRadius: "10px",
    background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
  },
};

const whiteInputStyles = {
  "& .MuiInputBase-input": {
    color: "#e2e8f0",
  },

  "& .MuiInputLabel-root": {
    color: "#cbd5e1",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#f8fafc",
  },

  "& .MuiOutlinedInput-root": {
    background: "rgba(15, 23, 42, 0.6)",
    borderRadius: "10px",
    "& fieldset": {
      borderColor: "rgba(148, 163, 184, 0.35)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(96, 165, 250, 0.65)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#60a5fa",
      boxShadow: "0 0 0 1px rgba(96,165,250,0.2)",
    },
  },

  "& .MuiSvgIcon-root": {
    color: "#e2e8f0",
  },
};

const darkNeonMenuProps = {
  MenuProps: {
    PaperProps: {
      sx: {
        bgcolor: "#0f172a",
        color: "#e2e8f0",
        borderRadius: 2,
        border: "1px solid rgba(96,165,250,0.25)",
        boxShadow: "0 16px 32px rgba(15,23,42,0.52)",
        maxHeight: 260,
        overflow: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(96, 165, 250, 0.8) transparent",
        "&::-webkit-scrollbar": {
          width: 8,
          background: "transparent",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background:
            "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(59,130,246,0.55))",
          borderRadius: 999,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
          boxShadow: "0 0 12px rgba(96,165,250,0.35)",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background:
            "linear-gradient(180deg, rgba(125,211,252,0.98), rgba(59,130,246,0.7))",
        },
        "& .MuiMenuItem-root": {
          color: "#e2e8f0",
          fontSize: 13,
          minHeight: 36,
          paddingY: 0.75,
          "&:hover": { bgcolor: "rgba(96,165,250,0.18)" },
          "&.Mui-selected": {
            bgcolor: "rgba(96,165,250,0.22)",
            color: "#f8fafc",
          },
        },
      },
    },
    MenuListProps: {
      sx: {
        padding: 0,
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(96, 165, 250, 0.8) transparent",
        "&::-webkit-scrollbar": {
          width: 8,
          background: "transparent",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background:
            "linear-gradient(180deg, rgba(96,165,250,0.95), rgba(59,130,246,0.55))",
          borderRadius: 999,
          border: "2px solid transparent",
          backgroundClip: "padding-box",
          boxShadow: "0 0 12px rgba(96,165,250,0.35)",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background:
            "linear-gradient(180deg, rgba(125,211,252,0.98), rgba(59,130,246,0.7))",
        },
      },
    },
  },
};

const dashboardPalette = {
  primary: "#6366f1",
  primarySoft: "#8b5cf6",

  success: "#4ade80",
  successSoft: "#22c55e",

  warning: "#fbbf24",
  warningSoft: "#f59e0b",

  danger: "#f87171",
  dangerSoft: "#ef4444",

  info: "#60a5fa",

  textMain: "#e5e7eb",
  textSoft: "#9ca3af",

  same: "rgba(31, 41, 55, 0.8)",

  surface: "rgba(255,255,255,0.04)",
  surfaceBorder: "rgba(255,255,255,0.08)",
};

const glassSurface = {
  p: 3,
  borderRadius: "10px",
  // background: dashboardPalette?.surface,
  background: dashboardPalette?.same,
  backdropFilter: "blur(10px)",
  border: `1px solid ${dashboardPalette?.surfaceBorder}`,
  // backgroundColor: "rgba(31,41,55,0.8)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
};

const TabPanel = ({ children, value, index }) =>
  value === index && <Box>{children}</Box>;

const KpiCard = ({
  title,
  secondText,
  value,
  secondValue = 0,
  color = "#6366f1",
  sxPropsContainer = {},
  sxPropsLetter = {},
}) => {
  const hasSecondary = Boolean(secondText);

  return (
    <Paper
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        p: 3,
        borderRadius: "10px",
        background: dashboardPalette?.same,
        backdropFilter: "blur(10px)",
        border: `1px solid ${color}40`,
        boxShadow: `0 10px 30px ${color}20`,
        transition: "0.3s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: `0 15px 40px ${color}50`,
        },
        ...sxPropsContainer,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          color,
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          ...sxPropsLetter,
        }}
      >
        {title}{" "}
        {hasSecondary && <AnimatedCounter value={value ?? 0} color={color} />}
      </Typography>
      {!hasSecondary && (
        <Typography variant="h4" fontWeight="bold" sx={{ color, mt: 1 }}>
          <AnimatedCounter value={value ?? 0} color={color} />
        </Typography>
      )}

      {hasSecondary && (
        <Typography
          variant="subtitle2"
          sx={{
            color,
            fontSize: "12px",
            ...sxPropsLetter,
          }}
        >
          {secondText}{" "}
          <AnimatedCounter value={secondValue ?? 0} color={color} />
        </Typography>
      )}
    </Paper>
  );
};

/* ------------------------------ */
/* Componente principal           */
/* ------------------------------ */

export default function InformeGeneral() {
  const { userData } = useUser();
  const { user } = userData;

  /* ------------------------------ */
  /* Estados                        */
  /* ------------------------------ */

  const [tab, setTab] = useState(0);

  const [contactsData, setContactsData] = useState([]);
  const [contactsGraphData, setContactsGraphData] = useState([]);
  const [contactsActualStateData, setContactsActualStateData] = useState([]);
  const [clientsData, setClientsData] = useState([]);
  const [asocZoneData, setAsocZoneData] = useState([]);

  const [casesGraph, setCasesGraph] = useState([]);
  const [casesStatusData, setCasesStatusData] = useState([]);
  const [casesFlowMatrixData, setCasesFlowMatrixData] = useState([]);
  const [casesStatusOthers, setCasesStatusOthers] = useState([]);

  // contracts loading state is merged here
  const [loading, setLoading] = useState({
    contacts: false,
    clients: false,
    asocZone: false,
    cases: false,
    contracts: false,
  });
  const [initialSyncStarted, setInitialSyncStarted] = useState(false);
  const [initialSyncCompleted, setInitialSyncCompleted] = useState(false);
  const casesStatusRetryTimeoutRef = useRef(null);

  const [pagination, setPagination] = useState({
    contacts: { page: 1, pageCount: 1, perPage: 20 },
    clients: { page: 1, pageCount: 1, perPage: 10 },
    asocZone: { page: 1, pageCount: 1, perPage: 10 },
    contracts: { page: 1, pageCount: 1, perPage: 10 },
  });

  const [totalTableRegisters, setTotalTableRegisters] = useState({
    contacts: 0,
    clients: 0,
    asocZone: 0,
    contracts: 0,
  });
  const [apply, setApply] = useState(0);

  const [totalContactstTable, setTotalContactsTable] = useState(0);
  const [totalClientstTable, setTotalClientsTable] = useState(0);
  const [totalCasestTable, setTotalCasesTable] = useState(0);
  const [contractsData, setContractsData] = useState([]);
  const [contractsGraph, setContractsGraph] = useState([]);
  const [contractsByTitle, setContractsByTitle] = useState([]);
  const [totalContractstTable, setTotalContractsTable] = useState(0);

  /* ------------------------------ */
  /* Filtros                        */
  /* ------------------------------ */

  const [dateFromState, setDateFromState] = useState(
    dayjs().subtract(6, "month").format("YYYY-MM-DD"),
  );
  const [dateToState, setDateToState] = useState(dayjs().format("YYYY-MM-DD"));

  const [company, setCompany] = useState(() => {
    const companyId = user?.company_id || user?.company?.id || 0;
    const companyName =
      user?.company_name || user?.company?.company_name || "Seleccionar";
    return { id: companyId, company_name: companyName };
  });

  const [companiesList, setCompaniesList] = useState([
    { id: 0, company_name: "Seleccionar" },
  ]);

  const [kpiTotals, setKpiTotals] = useState({
    contacts: 0,
    total_contacts: {
      Cancelado: 0,
      Cliente: 0,
      Llamado: 0,
      Pte: 0,
      Total: 0,
    },
    clients: 0,
    total_clients: 0,
    cases: 0,
    total_cases: 0,
  });

  const [tableTotals, setTableTotals] = useState({
    // TABLA CONTACTOS
    table_total_contacts_page: {
      Cancelado: 0,
      Cliente: 0,
      Llamado: 0,
      Pte: 0,
      Total: 0,
    },
    table_total_contacts: {
      Cancelado: 0,
      Cliente: 0,
      Llamado: 0,
      Pte: 0,
      Total: 0,
    },
    table_total_contacts_historical: {
      Cancelado: 0,
      Cliente: 0,
      Llamado: 0,
      Pte: 0,
      Total: 0,
    },
    table_total_contacts_normal: {
      Cancelado: 0,
      Cliente: 0,
      Llamado: 0,
      Pte: 0,
      Total: 0,
    },
    table_contact_totals_clients_removed: {
      Cancelado: 0,
      Cliente: 0,
      Llamado: 0,
      Pte: 0,
      Total: 0,
    },

    //TABLA CLIENTES
    table_total_clients: 0,
    table_total_clients_historical: 0,
    table_total_clients_normal: 0,
    table_client_totals_clients_removed: 0,
    table_total_clients_page: 0,
    //TABLA EXPEDIENTES
    cases: 0,
    total_cases: 0,
    table_total_cases_page: 0,
    // TABLA CONTRATOS
    table_total_contracts_page: 0,
    table_total_contracts: 0,
  });

  const normalizePaginationPayload = (raw, fallback) => {
    const page = Number(raw?.current_page ?? fallback.page);
    const pageCount = Number(raw?.last_page ?? fallback.pageCount);
    const perPage = Number(raw?.per_page ?? fallback.perPage);

    return {
      page: Number.isFinite(page) && page > 0 ? page : fallback.page,
      pageCount:
        Number.isFinite(pageCount) && pageCount > 0
          ? pageCount
          : fallback.pageCount,
      perPage:
        Number.isFinite(perPage) && perPage > 0 ? perPage : fallback.perPage,
    };
  };

  const hasPaginationChanged = (current, next) => {
    return (
      current.page !== next.page ||
      current.pageCount !== next.pageCount ||
      current.perPage !== next.perPage
    );
  };

  const getInitialData = async () => {
    try {
      setLoading((p) => ({
        ...p,
        contacts: true,
        clients: true,
        asocZone: true,
      }));

      const res = await api.post(`${process.env.REACT_APP_API_URL}/general`);

      // console.log(res);

      const data = res.data;

      // Si vienen empresas, las seteamos
      if (data.companies && Array.isArray(data.companies)) {
        const defaultOption = { id: 0, company_name: "Seleccionar" };
        setCompaniesList([defaultOption, ...data.companies]);

        // Setear company por defecto si coincide con user.company_id
        if (user) {
          const defaultCompany = data.companies.find(
            (c) => c.id === company.id,
          ) || { id: 0, company_name: "Seleccionar" };
          setCompany(defaultCompany);
        }
      }
    } catch (error) {
      console.error("Error en getInitialData:", error);
    } finally {
      setLoading((p) => ({
        ...p,
        contacts: false,
        clients: false,
        asocZone: false,
      }));
    }
  };

  useEffect(() => {
    if (user) getInitialData();
  }, [user]);

  /* ------------------------------ */
  /* Fetch helpers                  */
  /* ------------------------------ */
  const normalizeContactsActualStateData = (rows = []) => {
    const statusMap = {
      0: "Pte",
      1: "Llamado",
    };

    const totals = Object.keys(statusMap).reduce((acc, key) => {
      acc[key] = 0;
      return acc;
    }, {});

    if (!Array.isArray(rows) || rows.length === 0) {
      return Object.entries(statusMap).map(([status, label]) => ({
        status: Number(status),
        label,
        total: 0,
      }));
    }

    rows.forEach((row) => {
      const status = Number(row?.status ?? row?.estado ?? row?.value ?? -1);
      const total = Number(row?.total ?? row?.count ?? 0);

      if (status in statusMap) {
        totals[String(status)] += total;
      }
    });

    return Object.entries(statusMap).map(([status, label]) => ({
      status: Number(status),
      label,
      total: Number(totals[String(status)] ?? 0),
    }));
  };

  const postFetch = async (url, page, setter, loadingKey, paginationKey) => {
    try {
      setLoading((p) => ({ ...p, [loadingKey]: true }));

      const res = await api.post(url, {
        page,
        dateFrom: dateFromState + " 00:00:00",
        dateTo: dateToState + " 23:59:59",
        companyId: company.id,
      });

      const payload = res.data;

      // console.log(payload);
      // CONTACTS
      if (payload?.contacts) {
        // NUMERO TOTAL DE REGISTROS EN LA TABLA
        setTotalTableRegisters((p) => ({
          ...p,
          contacts: payload?.contacts?.total,
        }));

        const contactsPageTotals = calculateContactsPageTotals(
          payload.contacts.data ?? [],
        );

        // setTotalContactsTable(payload?.column_totals?.Total); // total filtrado por fechas
        setTableTotals((p) => ({
          ...p,
          contacts: payload.total_contacts_all,
          table_total_contacts: payload.column_totals,
          table_total_contacts_historical: payload.totals_historical,
          table_total_contacts_normal: payload.totals_normal,
          table_contact_totals_clients_removed: payload.totals_clients_removed,
          table_total_contacts_page: contactsPageTotals,
        }));

        setter(payload.contacts.data ?? []);

        setPagination((p) => {
          const current = p[paginationKey] || {
            page: 1,
            pageCount: 1,
            perPage: 10,
          };
          const next = normalizePaginationPayload(payload.contacts, current);

          if (!hasPaginationChanged(current, next)) return p;

          return {
            ...p,
            [paginationKey]: next,
          };
        });
        return;
      }

      if (payload?.contactsgraph) {
        // NUMERO TOTAL DE REGISTROS EN LA TABLA
        setter(payload.contactsgraph ?? []);
        return;
      }

      if (payload?.actualstate) {
        setter(normalizeContactsActualStateData(payload.actualstate ?? []));
        return;
      }

      // CLIENTS
      if (payload.clients) {
        // NUMERO TOTAL DE REGISTROS EN LA TABLA
        setTotalTableRegisters((p) => ({
          ...p,
          clients: payload?.clients?.total,
        }));

        const clientsPageTotal = calculateClientsPageTotals(
          payload.clients.data ?? [],
        );

        // NUMERO TOTAL DE REGISTROS EN LA COLUMNA
        setTotalClientsTable(payload.total_table);

        setTableTotals((p) => ({
          ...p,
          clients: payload?.clients?.total,
          table_total_clients: payload.total_table,
          table_total_clients_historical: payload.totals_historical,
          table_total_clients_normal: payload.totals_normal,
          table_client_totals_clients_removed: payload.totals_clients_removed,
          table_total_clients_page: clientsPageTotal,
        }));

        setter(payload.clients.data ?? []);
        setPagination((p) => {
          const current = p[paginationKey] || {
            page: 1,
            pageCount: 1,
            perPage: 10,
          };
          const next = normalizePaginationPayload(payload.clients, current);

          if (!hasPaginationChanged(current, next)) return p;

          return {
            ...p,
            [paginationKey]: next,
          };
        });

        return;
      }

      // CASES ASOC ZONE
      const casesAsocZonePayload = payload.casesasoczone ?? payload.data;

      if (casesAsocZonePayload) {
        // NUMERO TOTAL DE REGISTROS EN LA TABLA
        setTotalTableRegisters((p) => ({
          ...p,
          asocZone: casesAsocZonePayload?.total,
        }));

        const casesPageTotal = calculateCasesPageTotals(
          casesAsocZonePayload.data ?? [],
        );

        setTableTotals((p) => ({
          ...p,
          cases: payload?.clients?.total,
          table_total_cases_page: casesPageTotal,
          table_total_casess_page: payload.total_table,
        }));

        // NUMERO TOTAL DE REGISTROS EN LA COLUMNA
        setTotalCasesTable(payload.total_table);
        setter(casesAsocZonePayload.data ?? []);
        setPagination((p) => {
          const current = p[paginationKey] || {
            page: 1,
            pageCount: 1,
            perPage: 10,
          };
          const next = normalizePaginationPayload(
            casesAsocZonePayload,
            current,
          );

          if (!hasPaginationChanged(current, next)) return p;

          return {
            ...p,
            [paginationKey]: next,
          };
        });

        return;
      }

      setter([]);
    } catch (error) {
      const err = error.response?.data;

      console.error("ERROR:", err?.error);
      console.error("MESSAGE:", err?.message);
      console.error("FILE:", err?.file);
      console.error("LINE:", err?.line);
      console.error("TRACE:", err?.trace);
    } finally {
      setLoading((p) => ({ ...p, [loadingKey]: false }));
    }
  };

  /* ------------------------------ */
  /* Fetch gráficos                 */
  /* ------------------------------ */
  const fetchContracts = async (page = 1) => {
    try {
      setLoading((p) => ({ ...p, contracts: true }));

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/contractssigned`,
        {
          page,
          dateFrom: dateFromState + " 00:00:00",
          dateTo: dateToState + " 23:59:59",
          companyId: company.id,
        },
      );

      const payload = res.data;
      // console.log("CONTRACTS SIGNED:", payload);

      const paginated = payload?.contractssigned;

      if (paginated) {
        setTotalTableRegisters((p) => ({
          ...p,
          contracts: paginated?.total ?? 0,
        }));

        const contractsPageTotal = (paginated?.data ?? []).reduce(
          (acc, row) => acc + Number(row.total || 0),
          0,
        );

        setTableTotals((p) => ({
          ...p,
          table_total_contracts_page: contractsPageTotal,
          table_total_contracts: payload?.total_table ?? 0,
        }));

        setTotalContractsTable(payload?.total_table ?? 0);

        setContractsData(paginated?.data ?? []);
        setPagination((p) => {
          const current = p.contracts;
          const next = normalizePaginationPayload(paginated, current);

          if (!hasPaginationChanged(current, next)) return p;

          return {
            ...p,
            contracts: next,
          };
        });
      }

      setContractsGraph(payload?.contractsgraph ?? []);
      setContractsByTitle(payload?.contractsbytitle ?? []);
    } catch (error) {
      const err = error.response?.data;
      console.error("ERROR contracts:", err?.error || error.message);
    } finally {
      setLoading((p) => ({ ...p, contracts: false }));
    }
  };

  const fetchKpisTotals = async () => {
    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/kpistotals`,
        {
          company_id: company?.id,
        },
      );

      const data = res?.data?.data ?? {};

      // console.log("KPI TOTALS RAW RESPONSE:", data);

      const normalized = {
        contacts: data?.contacts_total_db ?? 0,
        cases: data?.cases_total_db ?? 0,
        average: data?.monthly_average ?? 0,
        clients: data?.clients_total_db ?? 0,
        clients_historical: data?.clients_total_historical_db,
        contacts_historical: data?.contacts_total_historical_db,
      };

      // console.log("KPI TOTALS NORMALIZED:", normalized);

      setKpiTotals((prev) => ({
        ...prev,
        ...normalized,
      }));
    } catch (error) {
      console.error("ERROR AXIOS:", error);

      if (error.response) {
        console.error("STATUS:", error.response.status);
        console.error("DATA:", error.response.data);
        console.error("HEADERS:", error.response.headers);
      } else if (error.request) {
        console.error("NO RESPONSE:", error.request);
      } else {
        console.error("REQUEST ERROR:", error.message);
      }

      setKpiTotals((prev) => ({
        ...prev,
        contacts: 0,
        cases: 0,
        average: 0,
        clients: 0,
      }));
    }
  };

  const fetchCasesGraph = async () => {
    const res = await api.post(`${process.env.REACT_APP_API_URL}/graphcases`, {
      dateFrom: dateFromState + " 00:00:00",
      dateTo: dateToState + " 23:59:59",
      companyId: company.id,
    });

    // console.log(res);

    const rows = res.data?.data ?? [];

    setCasesGraph(
      rows.map((i) => ({
        date: i.date,
        count: i.count,
      })),
    );
  };

  const fetchCasesByStatus = async (retry = 0) => {
    let keepPolling = false;

    try {
      if (retry === 0) {
        if (casesStatusRetryTimeoutRef.current) {
          clearTimeout(casesStatusRetryTimeoutRef.current);
        }

        setLoading((p) => ({ ...p, cases: true }));
      }

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/casesbystatus`,
        {
          dateFrom: dateFromState + " 00:00:00",
          dateTo: dateToState + " 23:59:59",
          companyId: company.id,
        },
      );

      // console.log(res);

      if (res.status === 202) {
        if (retry < 10) {
          keepPolling = true;
          casesStatusRetryTimeoutRef.current = setTimeout(
            () => fetchCasesByStatus(retry + 1),
            3000,
          );
        }
        return;
      }

      const raw = res.data?.CasesByStatus ?? [];
      const totalCases = raw.reduce((acc, item) => acc + Number(item.value), 0);

      setCasesStatusData(
        raw.map((i) => ({
          label: i.label,
          value:
            totalCases > 0
              ? Number(((i.value / totalCases) * 100).toFixed(2))
              : 0,
          realValue: Number(i.value),
        })),
      );
    } catch (e) {
      console.error(e);
    } finally {
      if (!keepPolling) {
        casesStatusRetryTimeoutRef.current = null;
        setLoading((p) => ({ ...p, cases: false }));
      }
    }
  };

  const fetchCasesFlowMatrix = async () => {
    try {
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/casesflowmatrix`,
        {
          dateFrom: dateFromState + " 00:00:00",
          dateTo: dateToState + " 23:59:59",
          companyId: company.id,
        },
      );

      setCasesFlowMatrixData(res.data?.data ?? []);
    } catch (error) {
      console.error("ERROR casesflowmatrix:", error);
      setCasesFlowMatrixData([]);
    }
  };

  /* ------------------------------ */
  /* Effects                        */
  /* ------------------------------ */

  useEffect(() => {
    if (!user) return;
    postFetch(
      `${process.env.REACT_APP_API_URL}/contactsgraph`,
      pagination.contacts.page,
      setContactsGraphData,
      "contactsgraph",
      "contactsgraph",
    );
  }, [apply]);

  useEffect(() => {
    if (!user) return;
    postFetch(
      `${process.env.REACT_APP_API_URL}/contactsactualstate`,
      pagination.contacts.page,
      setContactsActualStateData,
      "actualstate",
      "actualstate",
    );
  }, [apply]);

  useEffect(() => {
    if (!user) return;
    postFetch(
      `${process.env.REACT_APP_API_URL}/clientsmonthasoc`,
      pagination.clients.page,
      setClientsData,
      "clients",
      "clients",
    );
  }, [apply, pagination.clients.page]);

  useEffect(() => {
    if (!user) return;
    postFetch(
      `${process.env.REACT_APP_API_URL}/casesasoczone`,
      pagination.asocZone.page,
      setAsocZoneData,
      "asocZone",
      "asocZone",
    );
  }, [apply, pagination.asocZone.page]);

  useEffect(() => {
    if (!user) return;
    fetchCasesGraph();
  }, [apply]);

  useEffect(() => {
    if (!user) return;
    fetchCasesByStatus();
    fetchCasesFlowMatrix();
  }, [apply]);

  useEffect(() => {
    if (!user) return;
    fetchKpisTotals();
  }, [apply]);

  useEffect(() => {
    return () => {
      if (casesStatusRetryTimeoutRef.current) {
        clearTimeout(casesStatusRetryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchContracts(pagination.contracts.page);
  }, [apply, pagination.contracts.page]);

  useEffect(() => {
    if (!user) return;
    postFetch(
      `${process.env.REACT_APP_API_URL}/contactstable`,
      pagination.contacts.page,
      setContactsData,
      "contacts",
      "contacts",
    );
  }, [apply, pagination.contacts.page]);

  const trackedInitialLoading =
    loading.contacts ||
    loading.clients ||
    loading.asocZone ||
    loading.cases ||
    loading.contracts;

  useEffect(() => {
    if (!user || initialSyncCompleted) return;

    if (trackedInitialLoading) {
      setInitialSyncStarted(true);
      return;
    }

    if (initialSyncStarted) {
      setInitialSyncCompleted(true);
    }
  }, [user, trackedInitialLoading, initialSyncStarted, initialSyncCompleted]);

  const showInitialSync =
    initialSyncStarted && !initialSyncCompleted && trackedInitialLoading;

  const totalRequests = 5;

  const completedRequests = [
    !loading.contacts,
    !loading.clients,
    !loading.asocZone,
    !loading.cases,
    !loading.contracts,
  ].filter(Boolean).length;

  const progress = Math.round((completedRequests / totalRequests) * 100);

  // useEffect(() => {
  //   Swal.fire({
  //     title: "Recalculando datos...",
  //     html: `
  //     <p style="color: #e5e7eb;">
  //       Sincronizando métricas... <br/>
  //       <b style="color: #e5e7eb;">
  //         Esto puede tardar unos segundos
  //       </b>
  //     </p>
  //   `,
  //     background: "#020617",
  //     color: "#e5e7eb",
  //     allowOutsideClick: false,
  //     didOpen: () => {
  //       Swal.showLoading();
  //     },
  //   });
  // }, [apply, loading]);

  // useEffect(() => {
  //   if (!isAnyLoading) {
  //     Swal.close();
  //   }
  // }, [loading]);

  const calculateContactsPageTotals = (rows = []) => {
    return rows.reduce(
      (acc, row) => {
        acc.Pte += Number(row.Pte || 0);
        acc.Llamado += Number(row.Llamado || 0);
        acc.Cliente += Number(row.Cliente || 0);
        acc.Cancelado += Number(row.Cancelado || 0);
        acc.Total += Number(row.Total || 0);
        return acc;
      },
      {
        Pte: 0,
        Llamado: 0,
        Cliente: 0,
        Cancelado: 0,
        Total: 0,
      },
    );
  };

  const calculateClientsPageTotals = (rows = []) => {
    return rows.reduce((acc, row) => {
      return acc + Number(row.total || 0);
    }, 0);
  };

  const calculateCasesPageTotals = (rows = []) => {
    return rows.reduce((acc, row) => {
      return acc + Number(row.total_cases || 0);
    }, 0);
  };

  /* ------------------------------ */
  /* KPIs                           */
  /* ------------------------------ */

  // const totalContacts = contactsData.length;
  // const totalClients = clientsData.length;

  // const totalCases = casesGraph.reduce((a, b) => a + b.count, 0);
  // console.log(totalCases);
  // const avgCases =
  //   casesGraph.length > 0 ? Math.round(totalCases / casesGraph.length) : 0;

  const avgCases = kpiTotals?.average > 0 ? kpiTotals?.average : 0;

  const handleChangeDateFromRange = (date) => {
    if (!date) return;
    setDateFromState(dayjs(date).format("YYYY-MM-DD"));
  };

  const handleChangeDateToRange = (date) => {
    if (!date) return;
    setDateToState(dayjs(date).format("YYYY-MM-DD"));
  };

  const dateRangeFilters = [
    {
      label: "Fecha entrada desde",
      value: dayjs(dateFromState),
      onChange: handleChangeDateFromRange,
      sx: {
        "& fieldset": {
          borderRight: "none",
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        },
      },
    },
    {
      label: "Fecha entrada hasta",
      value: dayjs(dateToState),
      onChange: handleChangeDateToRange,
      sx: {
        "& fieldset": {
          borderLeft: "none",
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        },
      },
      className: "rangeFilterDateRight",
    },
  ];

  const ChartSkeleton = ({ height = 300 }) => (
    <Skeleton
      variant="rectangular"
      height={height}
      sx={{
        borderRadius: "10px",
        bgcolor: "rgba(255,255,255,0.08)",
      }}
    />
  );

  /* ------------------------------ */
  /* Render                         */
  /* ------------------------------ */
  return (
    <div>
      <CommonSubHeader title="Informe General" />

      <Box
        sx={{
          m: "10px",
          borderRadius: "10px",
          p: 4,
          background: "linear-gradient(145deg, #000000, #0f172ac9)",
          // background: "linear-gradient(145deg, #111827, #0f172ac9)",
          boxShadow:
            "0 20px 40px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)",
          color: "#e5e7eb",
        }}
      >
        {/* Filtros */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 20,
            backdropFilter: "blur(10px)",
          }}
        >
          <Paper
            sx={{
              ...glassSurface,
              mb: 4,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "white",
                mb: "20px",
                textTransform: "upperCase",
                fontWeight: "600",
              }}
            >
              Nota: por defecto, la búsqueda de registros abarca un período de 6
              meses contado desde la fecha actual hacia atrás.{" "}
            </Typography>
            <Grid container>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="es"
              >
                {dateRangeFilters.map((item, i) => (
                  <Grid item xs={12} md={3} key={i} py={1}>
                    <DesktopDatePicker
                      label={item.label}
                      sx={{ ...item.sx, ...whiteInputStyles }}
                      value={item.value}
                      onChange={item.onChange}
                      format="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          className={item.className}
                          sx={whiteInputStyles}
                        />
                      )}
                    />
                  </Grid>
                ))}
              </LocalizationProvider>
              <Grid item xs={12} md={3} p={1}>
                <TextField
                  select
                  label="Empresa"
                  fullWidth
                  sx={whiteInputStyles}
                  SelectProps={darkNeonMenuProps}
                  value={company.id}
                  onChange={(e) =>
                    setCompany(
                      companiesList.find(
                        (c) => c.id === Number(e.target.value),
                      ),
                    )
                  }
                >
                  {companiesList.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.company_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={3} p={1}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                    boxShadow: "0 8px 20px rgba(99,102,241,0.4)",
                    "&:hover": {
                      boxShadow: "0 10px 30px rgba(139,92,246,0.6)",
                    },
                  }}
                  onClick={() => {
                    setContactsGraphData([]);
                    setCasesGraph([]);
                    setCasesStatusData([]);
                    setContractsGraph([]);
                    setContractsByTitle([]);
                    setLoading((p) => ({
                      ...p,
                      contacts: true,
                      clients: true,
                      asocZone: true,
                      cases: true,
                      contracts: true,
                    }));
                    setApply((prev) => prev + 1);
                    setPagination((p) => ({
                      ...p,
                      contacts: { ...p.contacts, page: 1 },
                      clients: { ...p.clients, page: 1 },
                      asocZone: { ...p.asocZone, page: 1 },
                      contracts: { ...p.contracts, page: 1 },
                    }));
                  }}
                >
                  Aplicar filtros
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
        {/* KPIs en render  */}

        {showInitialSync && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: "#9ca3af", mb: 1 }}>
              Sincronizando datos... {progress}%
            </Typography>

            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: "10px",
                backgroundColor: "rgba(255,255,255,0.1)",
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                },
              }}
            />
          </Box>
        )}

        <Grid container spacing={2} alignItems="stretch" mb={4}>
          <Grid item xs={12} md={3} sx={{ display: "flex" }}>
            <KpiCard
              title={"contactos totales empresa"}
              value={kpiTotals?.contacts}
              color={dashboardPalette?.info}
              secondValue={kpiTotals?.contacts_historical}
              secondText={"Registros en notas"}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex" }}>
            <KpiCard
              title={"clientes totales empresa"}
              value={kpiTotals?.clients}
              color={dashboardPalette?.warning}
              secondValue={kpiTotals?.clients_historical}
              secondText={"Registros en notas"}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex" }}>
            <KpiCard
              title={"casos totales empresa"}
              value={kpiTotals?.cases}
              color={dashboardPalette?.success}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex" }}>
            <KpiCard
              title="Expedientes promedio/Mes"
              value={avgCases ? Number(avgCases).toFixed(2) : "0.00"}
              color={dashboardPalette?.primary}
            />
          </Grid>
        </Grid>
        {/* Tabs */}
        <Box sx={{ width: "100%" }}>
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            variant="fullWidth"
            sx={fullWidthTabsStyles}
          >
            <Tab label="Contactos" />
            <Tab label="Clientes" />
            <Tab label="Expedientes" />
          </Tabs>
          <TabPanel value={tab} index={0}>
            <Fade in timeout={400}>
              <Grid container spacing={2}>
                <Grid item xs={12} xl={12} lg={12}>
                  {loading.contacts && contactsGraphData.length === 0 ? (
                    <ChartSkeleton height={320} />
                  ) : (
                    <ContactsLineChart contactsGraph={contactsGraphData} />
                  )}
                </Grid>
                {/* NO BORRAR: este bloque de estado actual se deja oculto intencionalmente; la lógica se conserva para contexto histórico y de negocio. */}
                <Grid item xs={12} xl={3} lg={3}>
                  {/*
                  {loading.contacts && contactsActualStateData.length === 0 ? (
                    <ChartSkeleton height={320} />
                  ) : (
                    <ContactsActualStateBarChart
                      contactsGraph={contactsActualStateData}
                    />
                  )}
                  */}
                </Grid>
                {/* TABLA */}

                {/* GRÁFICAS */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} xl={6.5}>
                      <Paper
                        sx={{
                          ...glassSurface,
                          p: 2,
                        }}
                      >
                        <Table
                          isLoading={loading.contacts}
                          totalsReport={[
                            {
                              fecha: (
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <Functions
                                    sx={{ fontSize: 22, color: "#fbbf24" }}
                                  />
                                  TOTAL PÁGINA
                                </span>
                              ),
                              message:
                                "Suma de los registros visibles en la página actual",
                              Pte:
                                tableTotals?.table_total_contacts_page?.Pte ??
                                0,
                              Llamado:
                                tableTotals?.table_total_contacts_page
                                  ?.Llamado ?? 0,
                              Cliente:
                                tableTotals?.table_total_contacts_page
                                  ?.Cliente ?? 0,
                              Cancelado:
                                tableTotals?.table_total_contacts_page
                                  ?.Cancelado ?? 0,
                              Total:
                                tableTotals?.table_total_contacts_page?.Total ??
                                0,
                            },

                            // NO QUITAR ESTE CODIGO, ES PARA REFERENCIA HISTORICA
                            // {
                            //   fecha: (
                            //     <span
                            //       style={{
                            //         display: "flex",
                            //         alignItems: "center",
                            //         gap: 6,
                            //       }}
                            //     >
                            //       <AddCircle
                            //         sx={{ fontSize: 22, color: "#22c55e" }}
                            //       />
                            //       Registros creados en Notas
                            //     </span>
                            //   ),
                            //   message:
                            //     "Registros creados a partir de notas principalmente",
                            //   Pte:
                            //     tableTotals?.table_total_contacts_historical
                            //       ?.Pte ?? 0,
                            //   Llamado:
                            //     tableTotals?.table_total_contacts_historical
                            //       ?.Llamado ?? 0,
                            //   Cliente:
                            //     tableTotals?.table_total_contacts_historical
                            //       ?.Cliente ?? 0,
                            //   Cancelado:
                            //     tableTotals?.table_total_contacts_historical
                            //       ?.Cancelado ?? 0,
                            //   Total:
                            //     tableTotals?.table_total_contacts_historical
                            //       ?.Total ?? 0,
                            // },
                            // {
                            //   fecha: (
                            //     <span
                            //       style={{
                            //         display: "flex",
                            //         alignItems: "center",
                            //         gap: 6,
                            //       }}
                            //     >
                            //       <AddCircle
                            //         sx={{ fontSize: 22, color: "#22c55e" }}
                            //       />
                            //       Registros Normales
                            //     </span>
                            //   ),
                            //   message:
                            //     "Registros normales observados en las tablas comunes",
                            //   Pte:
                            //     tableTotals?.table_total_contacts_normal?.Pte ??
                            //     0,
                            //   Llamado:
                            //     tableTotals?.table_total_contacts_normal
                            //       ?.Llamado ?? 0,
                            //   Cliente:
                            //     tableTotals?.table_total_contacts_normal
                            //       ?.Cliente ?? 0,
                            //   Cancelado:
                            //     tableTotals?.table_total_contacts_normal
                            //       ?.Cancelado ?? 0,
                            //   Total:
                            //     tableTotals?.table_total_contacts_normal
                            //       ?.Total ?? 0,
                            // },
                            // {
                            //   fecha: (
                            //     <span
                            //       style={{
                            //         display: "flex",
                            //         alignItems: "center",
                            //         gap: 6,
                            //       }}
                            //     >
                            //       <RemoveCircle
                            //         sx={{ fontSize: 22, color: "#ef4444" }}
                            //       />
                            //       Registros Duplicados
                            //     </span>
                            //   ),
                            //   message:
                            //     "Registros duplicados entre cliente e histórico",
                            //   Pte:
                            //     tableTotals
                            //       ?.table_contact_totals_clients_removed?.Pte ??
                            //     0,
                            //   Llamado:
                            //     tableTotals
                            //       ?.table_contact_totals_clients_removed
                            //       ?.Llamado ?? 0,
                            //   Cliente:
                            //     tableTotals
                            //       ?.table_contact_totals_clients_removed
                            //       ?.Cliente ?? 0,
                            //   Cancelado:
                            //     tableTotals
                            //       ?.table_contact_totals_clients_removed
                            //       ?.Cancelado ?? 0,
                            //   Total:
                            //     tableTotals
                            //       ?.table_contact_totals_clients_removed
                            //       ?.Total ?? 0,
                            // },

                            {
                              fecha: (
                                <span
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <Functions
                                    sx={{ fontSize: 22, color: "#3b82f6" }}
                                  />
                                  TOTAL TABLA
                                </span>
                              ),
                              message: "Suma total de registros",
                              Pte: tableTotals?.table_total_contacts?.Pte ?? 0,
                              Llamado:
                                tableTotals?.table_total_contacts?.Llamado ?? 0,
                              Cliente:
                                tableTotals?.table_total_contacts?.Cliente ?? 0,
                              Cancelado:
                                tableTotals?.table_total_contacts?.Cancelado ??
                                0,
                              Total:
                                tableTotals?.table_total_contacts?.Total ?? 0,
                            },
                          ]}
                          tableReports={true}
                          ///////////////////////
                          view="contacto"
                          viewTitle="contactos"
                          apiRoute="contacts"
                          mainView="informes"
                          //////
                          columns={columnsTableContacts}
                          data={contactsData}
                          checkBoxFlag={false}
                          sxPropContainer={{ width: "100%" }}
                          onPageChange={(e) =>
                            setPagination((p) => ({
                              ...p,
                              contacts: {
                                ...p.contacts,
                                page: e.selected + 1,
                              },
                            }))
                          }
                          pageCount={pagination.contacts.pageCount}
                          forcePage={pagination.contacts.page - 1}
                          objPerPage={pagination.contacts.perPage}
                          totalPagesNumber={totalTableRegisters?.contacts}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={12} xl={5.5}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {/* NO BORRAR: la comparación de estado actual queda comentada por decisión visual; no se eliminan ni la lógica ni el contexto de negocio. */}
                        {/*
                        {loading.contacts && contactsActualStateData.length === 0 ? (
                          <ChartSkeleton height={320} />
                        ) : (
                          <ContactsActualStateBarChart
                            contactsGraph={contactsActualStateData}
                          />
                        )}
                        */}
                        {loading.contacts && contactsGraphData.length === 0 ? (
                          <ChartSkeleton height={400} />
                        ) : (
                          <ContactsPieChart contactsGraph={contactsGraphData} />
                        )}
                        {loading.contacts && contactsGraphData.length === 0 ? (
                          <ChartSkeleton height={220} />
                        ) : (
                          <ContactsConversionBarChart
                            contactsGraph={contactsGraphData}
                          />
                        )}
                        {loading.contacts && contactsGraphData.length === 0 ? (
                          <ChartSkeleton height={260} />
                        ) : (
                          <ContactsStatusStackedBarChart
                            contactsGraph={contactsGraphData}
                          />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Fade>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {loading.contacts && contactsGraphData.length === 0 ? (
                  <ChartSkeleton height={320} />
                ) : (
                  <ClientsMonthlyLineChart contactsGraph={contactsGraphData} />
                )}
              </Grid>
              <Grid item xs={12}>
                <Fade in timeout={400}>
                  <Paper sx={{ ...glassSurface }}>
                    <Table
                      isLoading={loading.clients}
                      columns={columnsTableClients}
                      data={clientsData}
                      view="clientes"
                      viewTitle="clientes"
                      apiRoute="clients"
                      checkBoxFlag={false}
                      sxPropContainer={{ width: "100%" }}
                      mainView="informes"
                      onPageChange={(e) =>
                        setPagination((p) => ({
                          ...p,
                          clients: { ...p.clients, page: e.selected + 1 },
                        }))
                      }
                      pageCount={pagination.clients.pageCount}
                      forcePage={pagination.clients.page - 1}
                      objPerPage={pagination.clients.perPage}
                      totalPagesNumber={totalTableRegisters?.clients}
                      // TOTALS
                      tableReports={true}
                      totalsReport={[
                        // NO BORRAR SE USA PARA REGISTRO HISTORICO
                        // {
                        //   total: tableTotals?.table_total_clients_page,
                        //   year: (
                        //     <span
                        //       style={{
                        //         display: "flex",
                        //         alignItems: "center",
                        //         gap: 6,
                        //       }}
                        //     >
                        //       <Functions
                        //         sx={{ fontSize: 22, color: "#fbbf24" }}
                        //       />
                        //       TOTAL PÁGINA
                        //     </span>
                        //   ),
                        //   message:
                        //     "Suma de los registros visibles en la página actual",
                        // },
                        // {
                        //   total: tableTotals?.table_total_clients_historical,
                        //   year: (
                        //     <span
                        //       style={{
                        //         display: "flex",
                        //         alignItems: "center",
                        //         gap: 6,
                        //       }}
                        //     >
                        //       <AddCircle
                        //         sx={{ fontSize: 22, color: "#22c55e" }}
                        //       />
                        //       Registros creados en Notas
                        //     </span>
                        //   ),
                        //   message:
                        //     "Registros creados a partir de notas principalmente",
                        // },
                        // {
                        //   total: tableTotals?.table_total_clients_normal,
                        //   year: (
                        //     <span
                        //       style={{
                        //         display: "flex",
                        //         alignItems: "center",
                        //         gap: 6,
                        //       }}
                        //     >
                        //       <AddCircle
                        //         sx={{ fontSize: 22, color: "#22c55e" }}
                        //       />
                        //       Registros Normales
                        //     </span>
                        //   ),
                        //   message:
                        //     "Registros normales observados en las tablas comunes",
                        // },
                        {
                          total:
                            tableTotals?.table_client_totals_clients_removed,
                          message:
                            "Registros duplicados entre cliente e histórico",
                          year: (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <RemoveCircle
                                sx={{ fontSize: 22, color: "#ef4444" }}
                              />
                              Registros Duplicados
                            </span>
                          ),
                        },

                        {
                          total: totalClientstTable,
                          message: "Suma total de registros",
                          year: (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Functions
                                sx={{ fontSize: 22, color: "#3b82f6" }}
                              />
                              TOTAL TABLA
                            </span>
                          ),
                        },
                      ]}
                    />
                  </Paper>
                </Fade>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={tab} index={2}>
            {/* {loading.cases && (
              <LinearProgress
                sx={{
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                  },
                }}
              />
            )} */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {loading.cases && casesGraph.length === 0 ? (
                  <ChartSkeleton height={320} />
                ) : (
                  <CasesLineChart casesGraph={casesGraph} />
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Fade in timeout={400}>
                  <Paper sx={{ ...glassSurface }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        sx={{
                          color: "#e5e7eb",
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          mb: 0.5,
                        }}
                      >
                        Expedientes por asociación y zona
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#94a3b8", lineHeight: 1.5 }}
                      >
                        Agrupa los expedientes por la asociación y la zona del
                        cliente para detectar dónde se concentra más demanda o
                        actividad.
                      </Typography>
                    </Box>
                    <Table
                      isLoading={loading.asocZone}
                      columns={columnsTableAsocZone}
                      data={asocZoneData}
                      view="expedientes"
                      viewTitle="expedientes"
                      apiRoute="cases"
                      checkBoxFlag={false}
                      sxPropContainer={{ width: "100%" }}
                      mainView="informes"
                      onPageChange={(e) =>
                        setPagination((p) => ({
                          ...p,
                          asocZone: { ...p.asocZone, page: e.selected + 1 },
                        }))
                      }
                      pageCount={pagination.asocZone.pageCount}
                      forcePage={pagination.asocZone.page - 1}
                      objPerPage={pagination.asocZone.perPage}
                      totalPagesNumber={totalTableRegisters?.asocZone}
                      // TOTALS
                      tableReports={true}
                      totalsReport={[
                        {
                          asociations_relation: (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Functions
                                sx={{ fontSize: 22, color: "#fbbf24" }}
                              />
                              TOTAL PÁGINA
                            </span>
                          ),
                          message:
                            "Suma de los registros visibles en la página actual",
                          total_cases: tableTotals?.table_total_cases_page,
                        },
                        {
                          asociations_relation: (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Functions
                                sx={{ fontSize: 22, color: "#3b82f6" }}
                              />
                              TOTAL TABLA
                            </span>
                          ),
                          message: "Suma total de registros",
                          total_cases: totalCasestTable ?? 0,
                        },
                      ]}
                    />
                  </Paper>
                </Fade>
              </Grid>

              <Grid item xs={12} sx={{}}>
                <Fade in timeout={400}>
                  <Box>
                    {loading.cases && casesFlowMatrixData.length === 0 ? (
                      <ChartSkeleton height={260} />
                    ) : (
                      <CasesFlowStatusMatrixTable
                        matrixData={casesFlowMatrixData}
                      />
                    )}
                  </Box>
                </Fade>
              </Grid>
              {/* <Grid item xs={12}>
                {loading.contracts && contractsGraph.length === 0 ? (
                  <ChartSkeleton height={300} />
                ) : (
                  <ContractsLineChart contractsGraph={contractsGraph} />
                )}
              </Grid> */}

              <Grid item xs={12} md={6}>
                <Fade in timeout={400}>
                  <Paper sx={{ ...glassSurface }}>
                    <Table
                      isLoading={loading.contracts}
                      columns={columnsTableContracts}
                      data={contractsData}
                      view="expedientes"
                      viewTitle="contratos"
                      apiRoute="contractssigned"
                      checkBoxFlag={false}
                      sxPropContainer={{ width: "100%" }}
                      mainView="informes"
                      onPageChange={(e) =>
                        setPagination((p) => ({
                          ...p,
                          contracts: { ...p.contracts, page: e.selected + 1 },
                        }))
                      }
                      pageCount={pagination.contracts.pageCount}
                      forcePage={pagination.contracts.page - 1}
                      objPerPage={pagination.contracts.perPage}
                      totalPagesNumber={totalTableRegisters?.contracts}
                      tableReports={true}
                      totalsReport={[
                        {
                          fecha: (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Functions
                                sx={{ fontSize: 22, color: "#fbbf24" }}
                              />
                              TOTAL PÁGINA
                            </span>
                          ),
                          contract_title:
                            "Suma de firmas visibles en la página actual",
                          total: tableTotals?.table_total_contracts_page ?? 0,
                        },
                        {
                          fecha: (
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Functions
                                sx={{ fontSize: 22, color: "#3b82f6" }}
                              />
                              TOTAL TABLA
                            </span>
                          ),
                          contract_title: "Suma total de firmas",
                          total: totalContractstTable ?? 0,
                        },
                      ]}
                    />
                  </Paper>
                </Fade>
              </Grid>

              <Grid item xs={12} md={6}>
                <Fade in timeout={400}>
                  <Paper sx={{ display: "contents" }}>
                    {loading.contracts && contractsByTitle.length === 0 ? (
                      <ChartSkeleton height={350} />
                    ) : (
                      <ContractsPieChart contractsByTitle={contractsByTitle} />
                    )}

                    {(loading.cases && casesGraph.length === 0) ||
                    (loading.contracts && contractsGraph.length === 0) ? (
                      <ChartSkeleton height={360} />
                    ) : (
                      <Box sx={{ mt: 2 }}>
                        <CasesVsContractsLineChart
                          casesGraph={casesGraph}
                          contractsGraph={contractsGraph}
                        />
                      </Box>
                    )}
                  </Paper>
                </Fade>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
      </Box>
    </div>
  );
}
